import React, { useState, useRef } from 'react';
import { Container, Col, Row } from 'react-grid-system';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';

import _ from 'lodash';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { PDFViewer } from '@react-pdf/renderer';

import { EVENT } from '../../globals';

import { useApp } from '../_libs/hooks/useApp';
import { useMicrosoft } from '../_libs/hooks/useMicrosoft';
import Navigation from '../molecules/Navigation';
import ErrorBoundary from '../organisms/ErrorBoundary';
import LogoDragAndDrop from '../organisms/LogoDragAndDrop';
import ReportCard from '../templates/pdf/pages/report_summary/ReportCard';
import uploadLogoFileWithOptions from '../_libs/utils/uploadLogoFileWithOptions';

const ReportCardPageRow = ({
  description = null,
  label,
  htmlFor = null,
  element,
  error = null,
  required = null,
}): JSX.Element => {
  return (
    <Row align="center" style={{
      paddingTop: '1.5rem',
      paddingBottom: '1.5rem',
      borderBottom: '1px solid rgba(255,255,255,0.25)',
    }}>
      <Col>
        <label htmlFor={htmlFor}>
          <strong>{label}</strong>
          {
            required != null
              && <span> (required)</span>
          }
        </label>
        {
          description
            && <p className="f-small mt-2 mb-0 opacity-50">{description}</p>
        }
      </Col>
      <Col>
        {element}
        {error}
      </Col>
    </Row>
  );
};

const ReportCardPage = (): JSX.Element => {
  const methods = useForm();
  const { userId } = useApp();
  const { graphData } = useMicrosoft();
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [summaryData, setSummaryData] = useState();
  const [error, setError] = useState('');
  const logoUrlRef = useRef(null);

  async function handleFormSubmission(data: FieldValues) {
    let logo_url;
    if (data.file) {
      logo_url = await 
        uploadLogoFileWithOptions(
          data.file,
          {
            container: graphData?.id,
          },
        ).then(
          (res: AxiosResponse)=> {
            return res;
          },
          (reason: any) => {
            console.error('Could not upload logo. Reason: ', reason);
          },
        );
    }
    const _data = _.cloneDeep(data);

    // Delete files uploaded via UI the endpoint will not accept them and they are not needed
    delete _data.file;
    delete _data.file_upload;

    // If the logo has been cleared, use black as the color. 
    if (!logo_url) {
      logoUrlRef.current = null;
      _data.color = 'rgb(0,0,0)';
    }
    axios
      .post('/generate_summary/', {
        ..._data,
        logo_url,
      })
      .then(
        (res: AxiosResponse) => {
          if (res.data.status !== 'OK') {
            console.error('<SummaryPage> ', res.data.message);
            setError(res.data.message);
            setShowPdfViewer(false);
          } else {
            setSummaryData({
              ..._data,
              logo_url,
              ...res.data.data,
              graphUserId: graphData?.id,
            });
            setShowPdfViewer(true);
            setError('');
            logoUrlRef.current = logo_url;

            // NOTE: Consider moving to /generate_summary/ to reduce another call to BE
            axios.post('/create_event/', {
              eventCode: EVENT.GenerateReportCard,
              userId,
              key: 'formValues',
              value: JSON.stringify({
                summary_type: 'report_card',
                ...methods.getValues(),
                logo_url: logoUrlRef.current,
              }),
            });
          }
        },
        (error: AxiosError) => {
          console.error(error);
          setError(JSON.stringify(error.toJSON()));
          setShowPdfViewer(false);
        },
      );
  }
  const formErrors = methods.formState.errors;
  return (
    <ErrorBoundary>
      <Navigation />
      <Container>
        <Row
          style={{
            minHeight: '95vh',
            paddingBottom: '4rem',
          }}
          align="center"
        >
          <Col>
            <Row
              align="center"
              style={{
                paddingTop: '1.5rem',
                paddingBottom: '1.5rem',
                borderBottom: '1px solid rgba(255,255,255,0.25)',
              }}
            >
              <Col>
                <h1 className="f-serif f-normal f-italic ma-0">Radian Report Card</h1>
              </Col>
              <Col>
                <p className="f-small ma-0 opacity-50">Generate a two-page report card for any ZIP code.</p>
              </Col>
            </Row>
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(handleFormSubmission)} className="f-medium">
                <ReportCardPageRow
                  label="Organization Name"
                  description="Names must be less than 57 characters long."
                  htmlFor="name"
                  element={
                    <input
                      className="block full-width"
                      {...methods.register('name', {
                          maxLength: {
                            value: 57,
                            message: 'Error: This Organization name is too long. Keep the name to 57 characters or less.'
                          },
                          required: {
                            value: true,
                            message: 'Error: Please enter an organization name.',
                          }
                        })
                      }
                    />
                  }
                  error={
                    (formErrors && formErrors.name)
                      && <p className="f-italic f-small mb-0">{formErrors.name.message.toString()}</p>
                  }
                  required
                />
                <ReportCardPageRow
                  label="ZIP Code"
                  htmlFor="zip"
                  element={
                    <input
                      className="block full-width"
                      type="number"
                      {...methods.register('zip', {
                        maxLength: 5,
                        minLength: 5,
                        pattern: /[0-9]/,
                        required: true,
                      })} />
                  }
                  error={
                    (formErrors && formErrors.zip)
                      && 
                        <p className="f-italic f-small mb-0">
                          Error: Please enter a 5-digit ZIP code.
                        </p>
                  }
                  required
                />
                <ReportCardPageRow
                  label="Radius"
                  htmlFor="radius"
                  element={
                    <div>
                      <div className="py-1">
                        <input type="radio" id="radius1" value={1} {...methods.register('radius')} />
                        <label htmlFor="radius1">1 mile</label>
                      </div>
                      <div className="py-1">
                        <input type="radio" id="radius10" value={10} {...methods.register('radius')} defaultChecked />
                        <label htmlFor="radius10">10 mile</label>
                      </div>
                      <div className="py-1">
                        <input type="radio" id="radius100" value={100} {...methods.register('radius')} />
                        <label htmlFor="radius100">100 mile</label>
                      </div>
                    </div>
                  }
                  required
                />
                {/* Required: Drag-to-drop interface */}
                {/* Nice to have: Process SVGs then Webp */}
                {/* https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/File_drag_and_drop */}
                <ReportCardPageRow
                  label="Logo"
                  description="If a logo is not used, the organization's name will be written on the summary and the report will be printed in shades of black."
                  htmlFor="logo"
                  element={
                    <LogoDragAndDrop />
                  } />
                {/* Required: Use black if no logo color selected */}
                <Row
                  style={{
                    paddingTop: '2rem',
                    paddingBottom: '2rem',
                  }}
                  align="center"
                >
                  <Col>
                    <p className="inline-block f-small f-italic ma-0 opacity-50">Tip: Change the form inputs to quickly update report cards (e.g. change brand colors).</p>
                  </Col>
                  <Col>
                    <button type="submit" className="ma-0">
                      {
                        (showPdfViewer)
                          ? 'Re-Generate Report Card'
                          : 'Generate Report Card'
                      }
                    </button>
                  </Col>
                </Row>
                <Row>
                  <Col></Col>
                  <Col>
                    {
                      error.trim().length > 0
                        && <p className="f-italic f-small mb-4">Server error: {error}</p>
                    }
                    {
                      Object.keys(formErrors).length > 0
                        && <p className="f-small f-italic ma-0 mb-4">Please fix the errors listed above and submit again.</p>
                    }
                  </Col>
                </Row>
              </form>
            </FormProvider>
            {
              showPdfViewer
                &&
                  <Row>
                    <Col>
                      <PDFViewer
                        style={{
                          width: '100%',
                          height: '50vh'
                        }}
                        showToolbar={true}
                      >
                        <ReportCard data={summaryData} printTemplate={true} />
                      </PDFViewer>
                    </Col>
                  </Row>
            }
          </Col>
        </Row>
      </Container>
    </ErrorBoundary>
  );
};

export default ReportCardPage;
